/**
 * QR scanning for the PDA screens. `scanQr()` opens the full-screen scanner
 * (rendered once by `ScannerOverlay`) and resolves with the text, a
 * cancellation or an error. Inside the Android shell the native ML Kit
 * scanner reads the camera; in a browser the web camera is used.
 */
export type ScanError = 'permission-denied' | 'unavailable';

export type ScanResult =
  { kind: 'text'; text: string } | { kind: 'cancelled' } | { kind: 'error'; reason: ScanError };

export interface ScannerState {
  open: boolean;
}

type Listener = (state: ScannerState) => void;

let pending: ((result: ScanResult) => void) | null = null;
let state: ScannerState = { open: false };
const listeners = new Set<Listener>();

function publish(next: ScannerState): void {
  state = next;
  for (const listener of listeners) listener(state);
}

export function subscribeScanner(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getScannerState(): ScannerState {
  return state;
}

/** Opens the scanner. A second call while open resolves the first as cancelled. */
export function scanQr(): Promise<ScanResult> {
  if (pending) finishScan({ kind: 'cancelled' });
  return new Promise<ScanResult>((resolve) => {
    pending = resolve;
    publish({ open: true });
  });
}

/** Called by the overlay when a backend produced a result. */
export function finishScan(result: ScanResult): void {
  const resolve = pending;
  pending = null;
  publish({ open: false });
  resolve?.(result);
}

export function cancelScan(): void {
  finishScan({ kind: 'cancelled' });
}

/** A backend starts the camera and returns how to stop it. */
export interface ScanBackendHandlers {
  onText: (text: string) => void;
  onError: (reason: ScanError) => void;
}
export type ScanBackend = (handlers: ScanBackendHandlers) => Promise<() => Promise<void> | void>;
